import React from 'react';
import { CheckCircleIcon, WarningIcon, WarningTwoIcon } from '@chakra-ui/icons';
import { Center, Flex, Text } from '@chakra-ui/react';

import { ItemsSummaryStatusText } from '../../../../common/components/ItemsSummaryModal/ItemsSummaryStatusText';
import { TimedProgress } from '../../../../common/components/TimedProgress/TimedProgress';
import { useHtmlTranslations, useTranslations } from '../../../../contexts/LocalizationContext';

export type ReturnItemsResultStatus = 'success' | 'partial' | 'failure';

type ReturnItemsSuccessBodyProps = {
  status?: ReturnItemsResultStatus;
  returnedCount: number;
  totalCount: number;
  showProgress: boolean;
  onAnimationComplete: () => void;
};

export const ReturnItemsSuccessBody = ({
  status = 'success',
  returnedCount,
  totalCount,
  showProgress,
  onAnimationComplete,
}: ReturnItemsSuccessBodyProps) => {
  const translations = useTranslations();
  const htmlTranslations = useHtmlTranslations();

  const failureCount = Math.max(totalCount - returnedCount, 0);

  const statusConfig = {
    success: {
      Icon: CheckCircleIcon,
      color: 'green.800',
      title: translations('return_items_result_success_title'),
    },
    partial: {
      Icon: WarningTwoIcon,
      color: 'orange.500',
      title: translations('return_items_result_partial_title'),
    },
    failure: {
      Icon: WarningIcon,
      color: 'red.600',
      title: translations('return_items_result_failure_title'),
    },
  } as const;

  const { Icon, color, title } = statusConfig[status];
  const summaryText =
    status !== 'success'
      ? translations('return_items_result_summary_text', {
          '{{successItems}}': returnedCount.toString(),
          '{{totalItems}}': totalCount.toString(),
        })
      : null;
  const summarySuccessItems = Array.from({ length: returnedCount });
  const summaryFailureItems = Array.from({ length: failureCount });

  return (
    <Center py={4}>
      <Flex direction="column" align="center" width="100%">
        <Flex align="center">
          <Icon boxSize="20px" color={color} mr={1} my="auto" />
          <Text fontSize={20} fontWeight="bold" p={4} color="grey.900">
            {title}
          </Text>
        </Flex>
        {summaryText ? (
          <Flex pb={6} width="100%">
            <ItemsSummaryStatusText
              summaryText={summaryText}
              successItems={summarySuccessItems}
              failureItems={summaryFailureItems}
            />
          </Flex>
        ) : null}

        {status === 'success' || status === 'partial' ? (
          <Text fontSize={20} align="center">
            {htmlTranslations('return_items_result_success_info', {
              '{{items}}': returnedCount.toString(),
            })}
          </Text>
        ) : null}

        {status === 'failure' ? (
          <Text fontSize={20} align="center">
            {htmlTranslations('return_items_result_failure_info')}
          </Text>
        ) : null}

        {showProgress ? (
          <TimedProgress show={showProgress} showCountdown={true} onAnimationComplete={onAnimationComplete} />
        ) : null}
      </Flex>
    </Center>
  );
};
